import { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://api.cloudnine.biz.id';

/**
 * Custom hook untuk mengelola storyboard notes (Kanban cards with items)
 */
export const useStoryboardNotes = (storyboardId) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchNotes = useCallback(async (showLoading = false) => {
        if (!storyboardId) return;
        if (showLoading) setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/storyboard_notes.php?storyboard_id=${storyboardId}`);
            const data = await response.json();
            if (data.success) {
                setNotes(data.data || []);
            } else {
                setError(data.error || 'Failed to fetch notes');
            }
        } catch (err) {
            setError(err.message || 'Network error');
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [storyboardId]);

    const silentRefetch = useCallback(() => fetchNotes(false), [fetchNotes]);

    // Add card (parent_id = null)
    const addCard = async (title) => {
        try {
            const response = await fetch(`${API_BASE}/storyboard_notes.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ storyboard_id: storyboardId, title }),
            });
            const result = await response.json();
            if (result.success) {
                await silentRefetch();
                return result.data;
            }
            return null;
        } catch (err) {
            console.error('Error adding card:', err);
            return null;
        }
    };

    // Add item under a card
    const addItem = async (parentId, itemData) => {
        try {
            const response = await fetch(`${API_BASE}/storyboard_notes.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    storyboard_id: storyboardId,
                    parent_id: parentId,
                    content: itemData.content || '',
                    image: itemData.image || null,
                }),
            });
            const result = await response.json();
            if (result.success) {
                await silentRefetch();
                return result.data;
            }
            return null;
        } catch (err) {
            console.error('Error adding item:', err);
            return null;
        }
    };

    // Update any note (card title, item content, image)
    const updateNote = async (id, updateData) => {
        try {
            const response = await fetch(`${API_BASE}/storyboard_notes.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, ...updateData }),
            });
            const result = await response.json();
            if (result.success) {
                await silentRefetch();
                return result.data;
            }
            return null;
        } catch (err) {
            console.error('Error updating note:', err);
            return null;
        }
    };

    // Delete note
    const deleteNote = async (id) => {
        try {
            const response = await fetch(`${API_BASE}/storyboard_notes.php?id=${id}&action=delete`, {
                method: 'POST',
            });
            const result = await response.json();
            if (result.success) {
                await silentRefetch();
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error deleting note:', err);
            return false;
        }
    };

    // Move item logic (keep for kanban functionality)
    const moveItem = async (itemId, fromCardId, toCardId) => {
        try {
            await fetch(`${API_BASE}/storyboard_notes.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: itemId, parent_id: toCardId }),
            });
            await silentRefetch();
            return true;
        } catch (err) {
            console.error('Error moving item:', err);
            return false;
        }
    };

    // Toggle Pin Note
    const togglePinNote = async (id) => {
        try {
            const response = await fetch(`${API_BASE}/storyboard_notes.php?id=${id}&action=toggle_pin`, {
                method: 'POST',
            });
            const result = await response.json();
            if (result.success) {
                await silentRefetch();
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error toggling pin:', err);
            return false;
        }
    };

    // Upload file helper
    const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        const res = await fetch(`${API_BASE}/upload.php`, { method: 'POST', body: formData });
        const text = await res.text();
        const result = JSON.parse(text);
        if (result.success) return result.url;
        throw new Error(result.error || 'Upload failed');
    };

    useEffect(() => {
        fetchNotes(true);
    }, [fetchNotes]);

    return {
        notes,
        loading,
        error,
        refetch: () => fetchNotes(true),
        addCard,
        addItem,
        updateNote,
        deleteNote,
        togglePinNote,
        moveItem,
        uploadFile,
    };
};
